import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { profilePatch } from '../data/memberProfile';
import { MODULE_IDS } from '../data/trainingModules';
import { SlidesButton } from './SlideDeck';   // ruling 311
import { trainingDeck } from '../data/slideDecks';

// TRAINING VIDEO PLAYER - Bob's ruling, 2026-09-27 (task list section 481):
//   "the On-boarding videos ... begin automatically 3 seconds after the person first appears on the
//    platform - video and audio on - with the option to listen without the video, watch with the audio,
//    or turn it off for that module only."
// So: 3 s after the screen opens the video starts, picture AND sound. Three choices, always visible:
//   "Watch with sound" (the default), "Listen only" (picture hidden, sound continues), and
//   "Turn off for this module" - remembered for THIS module only; other modules are not affected.
// A browser will not start SOUND on a page the person has not yet touched. When that happens the video
// starts muted with captions and one button: "Turn on sound". One tap and it plays as ruled.
//
// RULING 245 item 1 (Bob, 2026-09-28, task list section 492): for a SIGNED-IN person the choice is kept on
// his ACCOUNT - users/{uid}.training.{module} = { off, watched } - so it holds on every computer and phone.
// The write goes through profilePatch() like every users/ write, and firestore.rules lets the owner change
// that one field and nothing else (module ids from src\data\trainingModules.js). This browser keeps a copy
// too, which is all an applicant (not signed in) has. If the account cannot be read or written, the browser
// copy still works and the video still plays - it never blocks a screen.
// Captions (.vtt) are always attached: a member who cannot hear it can still follow it.
//
// RULING 275 (Bob, 2026-10-03): "All videos play only once on their own, the first time that they are
// visited, and then they just shrink down to something to be clicked. There should also be a master button
// thats very visable to turn all the videos off."
//   1. A video opens on its own ONCE: the moment it opens by itself it is marked watched, so the next visit
//      shows only the small button - whether or not he watched it to the end or closed it early.
//   2. One master switch, "Turn all videos off", sits on the open player and on the small button. It sets
//      off for EVERY module id in MODULE_IDS (the same keys firestore.rules already allows - no rules change)
//      on the account and in this browser. While all are off, only one small "Videos are off - turn on" chip
//      remains, so the choice can be undone from any screen.

const KEY = (module) => 'ycp.training.' + module;
const readPref = (module) => { try { return JSON.parse(localStorage.getItem(KEY(module)) || '{}'); } catch { return {}; } };
const writePref = (module, v) => { try { localStorage.setItem(KEY(module), JSON.stringify(v)); } catch { /* private window: nothing kept */ } };

function saveToAccount(uid, module, v) {
  if (!uid) return;
  setDoc(doc(db, 'users', uid), profilePatch({ training: { [module]: v } }), { merge: true })
    .catch(() => { /* the browser copy stands; the account copy is retried on the next choice */ });
}

// RULING 275 item 2: every module at once, one write to the account.
const allOffNow = () => MODULE_IDS.every((m) => !!readPref(m).off);
function setAll(uid, off) {
  const training = {};
  MODULE_IDS.forEach((m) => {
    const next = { ...readPref(m), off, watched: true };
    writePref(m, next);
    training[m] = { off: !!next.off, watched: true };
  });
  if (uid) {
    setDoc(doc(db, 'users', uid), profilePatch({ training }), { merge: true }).catch(() => {});
  }
}

export default function TrainingVideo({ module, title, src, captions, delayMs = 3000, uid = null }) {
  const [pref, setPref] = useState(() => readPref(module));
  const [ready, setReady] = useState(!uid);           // signed in: wait for the account's choice first
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('watch');          // 'watch' = picture + sound, 'listen' = sound only
  const [blocked, setBlocked] = useState(false);      // browser refused to start sound
  const [allOff, setAllOff] = useState(() => allOffNow());
  const ref = useRef(null);

  // Signed in: the account's choice wins over this browser's (it is the one he made on purpose, anywhere).
  useEffect(() => {
    if (!uid) return undefined;
    let live = true;
    getDoc(doc(db, 'users', uid))
      .then((snap) => {
        if (!live) return;
        const acct = (snap.exists() && snap.data().training && snap.data().training[module]) || null;
        if (acct) { const merged = { ...readPref(module), ...acct }; writePref(module, merged); setPref(merged); }
        setReady(true);
      })
      .catch(() => { if (live) setReady(true); });
    return () => { live = false; };
  }, [uid, module]);

  // Start 3 seconds after the screen appears, unless this module was turned off or already watched.
  // RULING 275 item 1: opening on its own counts as the one time - marked watched as it opens.
  useEffect(() => {
    if (!ready || pref.off || pref.watched || allOffNow()) return undefined;
    const t = setTimeout(() => { keep({ watched: true }); setOpen(true); }, delayMs);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, module]);

  useEffect(() => {
    if (!open || !ref.current) return;
    const v = ref.current;
    v.muted = false;
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {                                  // sound refused before the first tap
        v.muted = true;
        setBlocked(true);
        v.play().catch(() => {});
      });
    }
  }, [open]);

  const keep = (patch) => {
    const next = { ...readPref(module), ...patch };
    writePref(module, next);
    setPref(next);
    saveToAccount(uid, module, { off: !!next.off, watched: !!next.watched });
  };
  const soundOn = () => { const v = ref.current; if (!v) return; v.muted = false; setBlocked(false); v.play().catch(() => {}); };
  const turnOff = () => { if (ref.current) ref.current.pause(); keep({ off: true }); setOpen(false); };
  const ended = () => { keep({ watched: true }); };
  const reopen = () => { keep({ off: false }); setMode('watch'); setOpen(true); };
  const off = !!pref.off;
  const allVideosOff = () => { if (ref.current) ref.current.pause(); setAll(uid, true); setPref(readPref(module)); setAllOff(true); setOpen(false); };
  const allVideosOn = () => { setAll(uid, false); setPref(readPref(module)); setAllOff(false); };
  const master = {
    background: '#7A1F1F', color: '#FFF4F0', border: '1px solid #C0504D', borderRadius: 6, padding: '6px 10px',
    fontSize: 12, fontWeight: 700, cursor: 'pointer',
  };

  const btn = (active) => ({
    background: active ? '#E6A023' : 'transparent', color: active ? '#0D1B2A' : '#C9D6E3',
    border: '1px solid ' + (active ? '#E6A023' : '#2B5C8A'), borderRadius: 6, padding: '6px 10px',
    fontSize: 12, fontWeight: 600, cursor: 'pointer',
  });

  if (!open) {
    if (allOff) {
      return (
        <button onClick={allVideosOn} title="All training videos are off. Turn them back on (they will not start by themselves)."
          style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 900, ...btn(false), background: '#13263A' }}>
          Videos are off - turn on
        </button>
      );
    }
    return (
      <div style={{ position: 'fixed', right: 18, bottom: 18, zIndex: 900, display: 'flex', gap: 6 }}>
        <button onClick={reopen} title={off ? 'Turned off for this module - play it anyway' : 'Play the ' + title}
          style={{ ...btn(false), background: '#13263A' }}>
          &#9654; {title}
        </button>
        <button onClick={allVideosOff} style={master} title="Turn off every training video on the platform">Turn all videos off</button>
      </div>
    );
  }

  // LISTEN ONLY (Bob, 2026-09-28: "when I click sound only this box should not be so obvious - close, shrink,
  // hide, do something with it and let me know what it is"). The panel shrinks to one small bar in the corner
  // that says what is playing; the sound keeps coming from the same (hidden) video element.
  const listening = mode === 'listen';
  const pauseOrPlay = () => { const v = ref.current; if (!v) return; if (v.paused) v.play().catch(() => {}); else v.pause(); };
  const close = () => { if (ref.current) ref.current.pause(); setOpen(false); };

  return (
    <div role={listening ? 'status' : 'dialog'} aria-label={title}
      style={listening
        ? { position: 'fixed', right: 18, bottom: 18, zIndex: 900, background: '#13263A', border: '1px solid #2B5C8A', borderRadius: 18,
            padding: '4px 6px 4px 12px', display: 'flex', alignItems: 'center', gap: 6, boxShadow: '0 4px 14px rgba(0,0,0,.35)', maxWidth: 'calc(100vw - 36px)' }
        : { position: 'fixed', right: 18, bottom: 18, zIndex: 900, width: 'min(460px, calc(100vw - 36px))',
            background: '#13263A', border: '1px solid #2B5C8A', borderRadius: 10, boxShadow: '0 8px 28px rgba(0,0,0,.45)', padding: 12 }}>
      {listening ? (
        <span style={{ color: '#C9D6E3', fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          title={'Listening only: ' + title + ' - the picture is hidden, the sound keeps playing'}>
          &#128266; Listening: {title}
        </span>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ color: '#F0F4F8', fontSize: 13 }}>{title}</strong>
          <button onClick={close} aria-label="Close for now"
            style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>&times;</button>
        </div>
      )}
      <video ref={ref} src={src} controls playsInline preload="auto" onEnded={ended}
        style={{ width: '100%', borderRadius: 6, background: '#000', display: listening ? 'none' : 'block' }}>
        {captions && <track kind="captions" src={captions} srcLang="en" label="English" default />}
      </video>
      {!listening && src && (   /* Ruling 311: the same scenes as slides */
        <div style={{ marginTop: 6 }}>
          <SlidesButton label="▶ Read it as slides" build={async () => {
            try { if (ref.current) ref.current.pause(); } catch (e) { /* nothing playing */ }
            const r = await fetch(String(src).replace(/\.mp4(\?.*)?$/, '_script.json'));
            if (!r.ok) throw new Error('no slides for this video yet');
            return trainingDeck(title, await r.json());
          }} />
        </div>
      )}
      {listening ? (
        <>
          <button style={{ ...btn(false), padding: '3px 8px' }} onClick={pauseOrPlay} title="Pause or resume the sound">&#9199;</button>
          <button style={{ ...btn(false), padding: '3px 8px' }} onClick={() => setMode('watch')} title="Show the picture again">Show</button>
          <button onClick={close} aria-label="Stop and close" title="Stop and close"
            style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 16, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>&times;</button>
        </>
      ) : null}
      {blocked && (
        <div style={{ marginTop: 8, color: '#FFD678', fontSize: 12 }}>
          Your browser starts videos without sound until you tap the page.{' '}
          <button style={btn(true)} onClick={soundOn}>Turn on sound</button>
        </div>
      )}
      {!listening ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          <button style={btn(true)} onClick={() => setMode('watch')}>Watch with sound</button>
          <button style={btn(false)} onClick={() => { setMode('listen'); soundOn(); }}>Listen only</button>
          <button style={btn(false)} onClick={turnOff}>Turn off for this module</button>
          <button style={master} onClick={allVideosOff}>Turn all videos off</button>
        </div>
      ) : null}
    </div>
  );
}
